import { useNavigate } from 'react-router-dom';
import { Edit, FileText, MessageCircle, Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, Button } from '@aaas/shared';
import { Space } from '../types';

interface SpaceCardProps {
  space: Space;
  onEdit: (space: Space) => void;
}

export function SpaceCard({ space, onEdit }: SpaceCardProps) {
  const navigate = useNavigate();

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't navigate if clicking on action buttons
    const target = e.target as HTMLElement;
    if (target.closest('button')) {
      return;
    }
    navigate(`/kms/spaces/${space.id}`);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <Card
      className="cursor-pointer transition-all duration-200 hover:shadow-md group h-full flex flex-col"
      onClick={handleCardClick}
    >
      <CardHeader className="pb-3">
        <div className="flex-1 min-w-0">
          <CardTitle className="text-lg text-gray-900 truncate mb-1">{space.name}</CardTitle>
        </div>
      </CardHeader>

      <CardContent className="pt-0 flex flex-col flex-grow">
        <div className="flex flex-col h-full">
          {/* Description - fixed height area */}
          <div className="flex-grow mb-3">
            {space.description ? (
              <div className="text-sm text-gray-600 line-clamp-3">{space.description}</div>
            ) : (
              <div className="text-sm text-gray-400 italic">No description provided</div>
            )}
          </div>

          {/* Bottom section - always at bottom */}
          <div className="mt-auto space-y-3">
            {/* Dates */}
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>Created {formatDate(space.created_at)}</span>
              <span>Updated {formatDate(space.updated_at)}</span>
            </div>

            {/* Action Icons */}
            <div className="flex items-center justify-center gap-1 pt-2 border-t border-gray-100">
              {/* Documents */}
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 hover:bg-blue-50 hover:text-blue-600"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/kms/spaces/${space.id}`);
                }}
                title="View Documents"
              >
                <FileText className="h-4 w-4" />
              </Button>

              {/* Edit */}
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 hover:bg-green-50 hover:text-green-600"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(space);
                }}
                title="Edit Space"
              >
                <Edit className="h-4 w-4" />
              </Button>

              {/* Chat */}
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 hover:bg-green-50 hover:text-green-600"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/kms/spaces/${space.id}/chat`);
                }}
                title="Chat with Documents"
              >
                <MessageCircle className="h-4 w-4" />
              </Button>

              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 hover:bg-purple-50 hover:text-purple-600 opacity-50 cursor-not-allowed"
                onClick={(e) => e.stopPropagation()}
                title="Permissions (Coming Soon)"
                disabled
              >
                <Users className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
